import React, { useEffect } from "react";
import { Box, Flex, useToast } from "@chakra-ui/react";
import { useChatbot } from "hooks/useChatbot";
import ChatHeader from "views/admin/chatbot/components/ChatHeader";
import ChatContainer from "views/admin/chatbot/components/ChatContainer";
import ChatInput from "views/admin/chatbot/components/ChatInput";

const ChatbotPage: React.FC = () => {
  const {
    conversationId,
    messages,
    isLoading,
    isStreaming,
    currentStreamingMessage,
    error,
    pendingFiles = [],
    isUploadingFile = false,
    sendMessage,
    startNewConversation,
    addPendingFile,
    removePendingFile,
  } = useChatbot();

  const toast = useToast();

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  const handleSendMessage = async (message: string) => {
    try {
      await sendMessage(message);
    } catch (err) {
      console.error("Failed to send message:", err);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      const isDuplicate = pendingFiles.some((f) => f.name === file.name);
      if (isDuplicate) {
        toast({
          title: "Duplicate file",
          description: `${file.name} is already added.`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      addPendingFile(file);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to add file. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRemoveFile = (fileName: string) => {
    removePendingFile(fileName);
    toast({
      title: "File Removed",
      description: "File has been removed from pending list.",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const isFileUploadAvailable = Boolean(addPendingFile && removePendingFile);

  const handleNewConversation = async () => {
    try {
      await startNewConversation();
      toast({
        title: "New Conversation",
        description: "Started a new conversation.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to start new conversation:", err);
      toast({
        title: "Error",
        description: "Failed to start new conversation. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      flex={1}
      flexDir="row"
      h={"100%"}
      pt={{ base: "130px", md: "80px", xl: "80px" }}
    >
      <Flex
        h="80vh"
        display="flex"
        flex={1}
        w="80%"
        mx="auto"
        justify="center"
        align="center"
      >
        <Box h="80vh" display="flex" flexDirection="column" w="80%">
          <ChatHeader
            onNewConversation={handleNewConversation}
            isLoading={isLoading}
            conversationId={conversationId}
          />

          <Flex direction="column" flex={1} h="0" overflowY="auto">
            <ChatContainer
              messages={messages}
              currentStreamingMessage={currentStreamingMessage}
              isStreaming={isStreaming}
              error={error}
              isLoading={isLoading}
            />

            <ChatInput
              onSendMessage={handleSendMessage}
              disabled={isStreaming || isLoading}
              placeholder="Ask me anything about customers, partners, or Gamelight..."
              {...(isFileUploadAvailable && {
                pendingFiles,
                isUploadingFile,
                onFileUpload: handleFileUpload,
                onRemoveFile: handleRemoveFile,
              })}
            />
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

export default ChatbotPage;
